import { useEffect, useMemo, useState } from 'react'
import { useSettings } from '../useSettings'
import { ModuleType, TimeCategory, TimeCategoryDefaultEnum } from '../../context/GlobalContext'
import { FileTypes, useFileModule } from '../module/useFileModule'

export type TimeCategoriesSetting = {
    hasTimeCategoriesModule: boolean
    timeCategories: TimeCategory[]
    defaultCategory: (emailInbound?: boolean) => TimeCategory
    defaultCategories: TimeCategory[]
    setCurrentFile: (file: FileTypes) => void
}

export function useTimeCategoriesSetting(): TimeCategoriesSetting {
    const settings = useSettings()

    const [currentFile, setCurrentFile] = useState<FileTypes>(undefined)
    const [timeCategories, setTimeCategories] = useState<TimeCategory[]>([])
    const [defaultCategories, setDefaultCategories] = useState<TimeCategory[]>([])
    const [defaultCategory, setDefaultCategory] = useState<TimeCategory>(undefined)

    const { has: hasInsolvency, setCurrentFile: setInsolvencyFile } = useFileModule(ModuleType.Insolvency)
    const { has: hasTimeCategories, setCurrentFile: setTimeCategoriesFile } = useFileModule(ModuleType.TimeCategories)
    const timeCategoriesIsActiveByDefault = useMemo<boolean>(
        () => settings?.modules?.find(mod => mod.type === ModuleType.TimeCategories)?.defaultActiveForFile,
        [settings],
    )

    useEffect(() => {
        setInsolvencyFile(currentFile)
        setTimeCategoriesFile(currentFile)
    }, [currentFile])

    useEffect(() => {
        if (hasInsolvency || hasTimeCategories || timeCategoriesIsActiveByDefault) {
            const _categories = (settings?.timeCategories || []).filter(
                category =>
                    (hasInsolvency && category.module === ModuleType.Insolvency) ||
                    (!hasInsolvency && category.module === undefined),
            )
            setTimeCategories(_categories)
            setDefaultCategory(_categories.find(category => TimeCategoryDefaultEnum.Default === category.defaultType))
        } else {
            setTimeCategories([])
            setDefaultCategory(undefined)
        }

        setDefaultCategories((settings?.timeCategories || []).filter(category => !category.module))
    }, [settings, currentFile, hasInsolvency, hasTimeCategories])

    return {
        hasTimeCategoriesModule: hasInsolvency || hasTimeCategories,
        timeCategories,
        defaultCategory: (emailInbound?: boolean) =>
            emailInbound === undefined
                ? defaultCategory
                : timeCategories.find(
                      category =>
                          category.defaultType &&
                          ((emailInbound && TimeCategoryDefaultEnum.DefaultEmailIn === category.defaultType) ||
                              (!emailInbound && TimeCategoryDefaultEnum.DefaultEmailOut === category.defaultType)),
                  ),
        defaultCategories,
        setCurrentFile,
    }
}
